import React from "react";
import type { CommentBody } from "@liveblocks/core";
import { useLiveblocksUser } from "./useLiveblocksUser";

type Props = {
  body: CommentBody;
};

const renderTextWithLinks = (text: string, keyPrefix: string): React.ReactNode[] => {
  // Local regex per call: no shared `lastIndex` state, so partial iterations
  // (e.g. an exception in a future caller) can't poison the next render.
  const urlRegex = /\b(?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]'"]/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  // `Array.from` avoids the TS downlevelIteration warning on the
  // `matchAll` IterableIterator under our current target.
  for (const match of Array.from(text.matchAll(urlRegex))) {
    const start = match.index ?? 0;
    const raw = match[0];
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    parts.push(
      <a
        key={`${keyPrefix}-link-${start}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#4f5b94] underline"
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
      </a>
    );
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : [text];
};

const MentionChip = ({ userId }: { userId: string }) => {
  const user = useLiveblocksUser(userId);
  return (
    <span className="text-[13px] font-medium text-[#4f5b94] bg-[#e2e2fc]/60 rounded px-1 py-[1px]">
      @{user.name || "user"}
    </span>
  );
};

export const CommentBodyRenderer: React.FC<Props> = ({ body }) => {
  if (!body?.content?.length) {
    return <p className="text-[13px] text-[#130f26]/60 italic">(empty comment)</p>;
  }

  return (
    <div className="flex select-text flex-col gap-1" style={{ userSelect: "text", WebkitUserSelect: "text" }}>
      {body.content.map((block, idx) => {
        if (block.type !== "paragraph") return null;
        return (
          <p
            key={idx}
            className="select-text text-[13px] leading-snug text-[#130f26] whitespace-pre-wrap break-words"
            style={{ userSelect: "text", WebkitUserSelect: "text", overflowWrap: "anywhere" }}
          >
            {block.children.map((inline, i) => {
              // Discriminate via `type` first — `CommentBodyLink` also has an
              // optional `text` field, so `"text" in inline` doesn't fully
              // narrow to the plain-text variant.
              if ("type" in inline && inline.type === "mention") {
                return <MentionChip key={i} userId={inline.id} />;
              }
              if ("type" in inline && inline.type === "link") {
                return (
                  <a
                    key={i}
                    href={inline.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4f5b94] underline"
                  >
                    {inline.text || inline.url}
                  </a>
                );
              }
              // Remaining case: `CommentBodyText` (no `type` field). Required
              // `text` plus optional formatting marks.
              const textInline = inline as {
                text: string;
                bold?: boolean;
                italic?: boolean;
                strikethrough?: boolean;
                code?: boolean;
              };
              let node: React.ReactNode = renderTextWithLinks(textInline.text, `${idx}-${i}`);
              if (textInline.bold) node = <strong>{node}</strong>;
              if (textInline.italic) node = <em>{node}</em>;
              if (textInline.strikethrough) node = <s>{node}</s>;
              if (textInline.code) node = <code className="font-mono text-[12px]">{node}</code>;
              return <React.Fragment key={i}>{node}</React.Fragment>;
            })}
          </p>
        );
      })}
    </div>
  );
};
